import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Bookmark } from 'lucide-react';
import { useScan } from '@/contexts/ScanContext';
import { useWatchlistContext } from '@/contexts/WatchlistContext';
import AnalyticsWithTransition from './AnalyticsWithTransition';
import Sidebar from './Sidebar';
import logo from '../assets/logo.png';

const STORAGE_KEY = 'sidebar-collapsed';

const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) !== 'false');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { state } = useScan();
  const { items: watchlistItems } = useWatchlistContext();
  const hasResults = Array.isArray(state.results) && state.results.length > 0;
  const showAnalytics = hasResults || state.isLoading;

  const handleToggle = () => {
    setCollapsed(c => {
      localStorage.setItem(STORAGE_KEY, String(!c));
      return !c;
    });
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop sidebar — fixed */}
      <div
        className={`hidden md:block fixed top-0 left-0 h-full z-40 flex-shrink-0 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
      >
        <Sidebar collapsed={collapsed} onToggle={handleToggle} />
      </div>

      {/* Mobile overlay + sidebar */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="md:hidden fixed top-0 left-0 h-full z-50 w-60">
            <Sidebar
              collapsed={false}
              onToggle={() => setMobileOpen(false)}
              onNavClick={() => setMobileOpen(false)}
            />
          </div>
        </>
      )}

      {/* Content column */}
      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ${collapsed ? 'md:ml-16' : 'md:ml-60'}`}
      >
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-gray-100 flex items-center gap-3 px-4 h-14 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="DomBase" className="w-7 h-7 rounded-md" />
            <span className="font-bold text-gray-900 text-sm">DomBase</span>
          </Link>
          <div className="ml-auto flex items-center gap-1">
            <Link
              to="/watchlist"
              className="relative p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label="Watchlist"
            >
              <Bookmark className="h-5 w-5" />
              {watchlistItems.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-yellow-400 text-white text-[9px] font-bold px-0.5 rounded-full flex items-center justify-center leading-none">
                  {watchlistItems.length}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Slim analytics bar — only when scanning or results present */}
        {showAnalytics && (
          <div className="bg-white border-b border-gray-100 px-4 py-2 flex-shrink-0">
            <AnalyticsWithTransition variant="header" show className="justify-center" />
          </div>
        )}

        <main className="flex-1 min-w-0 flex flex-col">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
